/**
 * Real-browser proof token (Phase B, 2026-08-22).
 *
 * A no-JavaScript scraper cannot run this code, so it never mints a token and
 * its paid searches are refused server-side (see api middleware/search_security
 * + services/human_gate). A real browser mints one here on first need and
 * attaches it to every paid search as the `x-mio-hp` header.
 *
 * Fail-SAFE: if minting ever fails (network blip, deploy rollover), we return
 * null and the search proceeds with no proof header. The server runs in WATCH
 * mode first (logs, never blocks) precisely so this path is proven harmless
 * before ENFORCE is turned on. This never throws and never blocks a search on
 * the client side.
 *
 * Browser-only: returns null during SSR (server renders use the internal-auth
 * bypass and are never gated).
 */

import { getOrCreateAnonSession } from "@/lib/anon-session";

function apiBaseUrl(): string {
  if (typeof window === "undefined") {
    return process.env.API_INTERNAL_URL ?? process.env.API_URL ?? "http://localhost:8000";
  }
  return process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
}

// Refresh a little before the real expiry so an in-flight search never carries a
// token that expires between mint and server-verify.
const REFRESH_SKEW_MS = 60_000;

let cachedToken: string | null = null;
let cachedExpiresAtMs = 0;
let inFlight: Promise<string | null> | null = null;

async function mint(): Promise<string | null> {
  try {
    const sticker = getOrCreateAnonSession();
    const res = await fetch(`${apiBaseUrl()}/api/human/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(sticker ? { "x-mio-session": sticker } : {}),
      },
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { token?: string; expires_in?: number };
    if (!data.token) return null;
    cachedToken = data.token;
    cachedExpiresAtMs = Date.now() + (data.expires_in ?? 1800) * 1000;
    return cachedToken;
  } catch {
    return null;
  }
}

/**
 * Return a valid proof token, minting or refreshing one if needed. Never throws;
 * returns null on the server or on any mint failure.
 */
export async function ensureProof(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  if (cachedToken && Date.now() < cachedExpiresAtMs - REFRESH_SKEW_MS) {
    return cachedToken;
  }
  // Dedupe concurrent mints (e.g. two searches fired quickly): one network call.
  if (!inFlight) {
    inFlight = mint().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/**
 * Proof header for a paid request. Spread into a fetch's headers:
 *   headers: { ...(await proofHeaders()) }
 * Returns {} when no token is available (SSR / mint failure), so the caller's
 * request shape is unchanged.
 */
export async function proofHeaders(): Promise<Record<string, string>> {
  const token = await ensureProof();
  return token ? { "x-mio-hp": token } : {};
}
